import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listFactories } from '../../services/factories';
import { listWorkshops } from '../../services/workshops';
import { listSections } from '../../services/sections';
import { listRooms } from '../../services/rooms';
import { listCameras } from '../../services/cameras';
import { listAnalyticsRuleOptions } from '../../services/violations';
import { listZones } from '../../services/zones';
import { encodeZoneOptionId } from '../../utils/scopeSelection';
import { withCameraWord } from '../../utils/hierarchyLabels';
import { useCameraNames } from '../../hooks/useCameraNames';

/*
 * Загрузка опций для ScopeFilter (ТЗ v4 §21 «parent ограничивает доступные
 * descendants»).
 *
 * Опции каждого уровня собираются только из выбранных родителей: пока объект
 * не выбран, список подразделений не запрашивается вообще. Это и есть
 * ACL-safe поведение — фронт не перебирает дерево целиком и показывает ровно
 * то, что backend отдал по выбранным ветвям.
 *
 * Каждая опция несёт parentId/parentName: две одноимённые камеры в разных
 * локациях иначе неразличимы (та же причина, что в таблицах FE-FIX21/22).
 */

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

/** 403/404 по зонам камеры — «у камеры нет доступных зон», а не сбой списка. */
const fetchCameraZones = (cameraId) => listZones(cameraId)
  .then((data) => data?.items || [])
  .catch((error) => {
    const status = error?.response?.status;
    if (status === 403 || status === 404) return [];
    throw error;
  });

const FETCHERS = {
  workshopIds: listWorkshops,
  sectionIds: listSections,
  roomIds: listRooms,
  cameraIds: (roomId) => listCameras(roomId),
  zoneIds: fetchCameraZones,
};

const PARENT_OF = {
  workshopIds: 'factoryIds',
  sectionIds: 'workshopIds',
  roomIds: 'sectionIds',
  cameraIds: 'roomIds',
  zoneIds: 'cameraIds',
};

const optionName = (item, levelKey) => {
  if (item?.name) return item.name;
  return levelKey === 'cameraIds' ? `Камера #${item?.id}` : `#${item?.id}`;
};

/** Корневой уровень: объекты доступны всегда, ACL применяет backend. */
const useFactoryOptions = (enabled) => {
  const [level, setLevel] = useState({ options: [], state: enabled ? 'loading' : 'idle' });
  const [token, setToken] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLevel({ options: [], state: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setLevel((prev) => ({ options: prev.options, state: 'loading' }));
    listFactories()
      .catch(() => listFactories())
      .then((data) => {
        if (cancelled) return;
        const options = normalizeList(data).map((item) => ({
          id: String(item.id),
          name: optionName(item, 'factoryIds'),
          parentId: '',
          parentName: '',
        }));
        setLevel({ options, state: options.length ? 'ready' : 'empty' });
      })
      .catch(() => { if (!cancelled) setLevel({ options: [], state: 'error' }); });
    return () => { cancelled = true; };
  }, [enabled, token]);

  const known = useMemo(
    () => Object.fromEntries(level.options.map((option) => [option.id, option])),
    [level.options]
  );

  return { ...level, known, reload: useCallback(() => setToken((value) => value + 1), []) };
};

/*
 * Опция дочернего уровня. Зона — пара camera:zone: zone_id уникален внутри
 * камеры, и без камеры в ключе выбор зоны расползался бы на одноимённые зоны
 * других камер.
 */
const toChildOption = (levelKey, item, parentId, parentName) => (levelKey === 'zoneIds'
  ? {
    id: encodeZoneOptionId(parentId, item.zone_id),
    name: item.display_name || item.name || String(item.zone_id),
    parentId,
    parentName,
  }
  : { id: String(item.id), name: optionName(item, levelKey), parentId, parentName });

/** Один повтор: backend периодически отвечает 504, и единичный сбой не должен
    превращать уровень в «часть списка не загрузилась». */
const fetchWithRetry = (fetchList, parentId) =>
  fetchList(parentId).catch(() => fetchList(parentId));

/**
 * Дочерний уровень: по одному запросу на каждого выбранного родителя,
 * результаты объединяются (union внутри уровня — ровно то же, что делает
 * backend со списком ID).
 *
 * Ответы кэшируются по родителю: клик по соседнему чекбоксу догружает только
 * нового родителя, а не перезапрашивает всю ветку заново (раньше каждый клик
 * заново слал запрос на каждого выбранного родителя, и часть из них падала).
 *
 * Если часть запросов упала, уровень остаётся рабочим, но помечается partial:
 * молча выдавать неполный список за полный нельзя. «Повторить» догружает
 * только упавших родителей.
 *
 * `known` — все когда-либо полученные опции уровня по ID: выбранный пункт,
 * чей родитель сейчас не загружен, показывается по имени, а не как «#19».
 *
 * Для зон (ТЗ v4 §19.1, «где применимо») камера без зон просто не даёт опций —
 * это нормальный случай, а не ошибка уровня.
 */
const useChildOptions = (levelKey, parentOptions, parentIds, enabled) => {
  const cacheRef = useRef(new Map());
  const [fetchState, setFetchState] = useState('idle');
  const [token, setToken] = useState(0);
  const parentKey = parentIds.join(',');
  const parentNames = useMemo(
    () => Object.fromEntries((parentOptions || []).map((option) => [option.id, option.name])),
    [parentOptions]
  );

  useEffect(() => {
    if (!enabled || !parentKey) {
      setFetchState('idle');
      return undefined;
    }
    const missing = parentKey.split(',').filter((id) => !cacheRef.current.has(id));
    if (missing.length === 0) {
      setFetchState('done');
      return undefined;
    }
    let cancelled = false;
    setFetchState('loading');
    const fetchList = FETCHERS[levelKey];
    Promise.all(missing.map((parentId) => fetchWithRetry(fetchList, parentId).then(
      (data) => { cacheRef.current.set(parentId, normalizeList(data)); },
      () => {}
    ))).then(() => {
      if (!cancelled) setFetchState('done');
    });
    return () => { cancelled = true; };
  }, [enabled, levelKey, parentKey, token]);

  const level = useMemo(() => {
    if (!enabled || !parentKey || fetchState === 'idle') {
      return { options: [], state: 'idle', partial: false };
    }
    const ids = parentKey.split(',');
    const loaded = ids.filter((id) => cacheRef.current.has(id));
    const options = loaded.flatMap((parentId) => cacheRef.current.get(parentId).map(
      (item) => toChildOption(levelKey, item, parentId, parentNames[parentId] || '')
    ));
    if (fetchState === 'loading') return { options, state: 'loading', partial: false };
    if (loaded.length === 0) return { options: [], state: 'error', partial: false };
    return {
      options,
      state: options.length ? 'ready' : 'empty',
      partial: loaded.length < ids.length,
    };
    // token входит в зависимости: кэш — ref, и после «Повторить» его новые
    // записи должны попасть в опции.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, levelKey, parentKey, parentNames, fetchState, token]);

  const known = useMemo(() => {
    const map = {};
    cacheRef.current.forEach((items, parentId) => items.forEach((item) => {
      const option = toChildOption(levelKey, item, parentId, parentNames[parentId] || '');
      map[option.id] = option;
    }));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  return { ...level, known, reload: useCallback(() => setToken((value) => value + 1), []) };
};

const RULE_SCOPE_PARAM = {
  factoryIds: 'factory_ids',
  workshopIds: 'workshop_ids',
  sectionIds: 'section_ids',
  roomIds: 'room_ids',
  cameraIds: 'camera_ids',
};

/**
 * Правила — отдельный уровень. Список приходит из ACL-aware роута аналитики
 * своей вкладки (BM-FE-TZ-002C): /analytics/violation-rules или
 * /analytics/business-rules. Правило другой категории в фильтр не попадает.
 * Роут принимает массивы ID уровней, поэтому выбранный scope передаётся
 * целиком. 401/403 — уровень недоступен, остальной фильтр работает дальше.
 */
/*
 * kind 'all' — правила обеих категорий (вкладка «События — Список» с обоими
 * типами событий). Роут без прав отдаёт 401/403 — берётся только второй;
 * уровень недоступен, только если закрыты оба.
 */
const loadRuleItems = (kind, params) => {
  if (kind !== 'all') return listAnalyticsRuleOptions(kind, params).then(({ items }) => items);
  return Promise.allSettled([
    listAnalyticsRuleOptions('violation', params),
    listAnalyticsRuleOptions('business', params),
  ]).then((results) => {
    const ok = results.filter((result) => result.status === 'fulfilled');
    if (ok.length === 0) throw results[0].reason;
    const failed = results.find((result) => result.status === 'rejected');
    const status = failed?.reason?.response?.status;
    if (failed && status !== 401 && status !== 403) throw failed.reason;
    return ok.flatMap((result) => result.value.items);
  });
};

const useRuleOptions = (scope, kind, enabled) => {
  const [level, setLevel] = useState({ options: [], state: 'idle' });
  const [token, setToken] = useState(0);

  const scopeParams = useMemo(() => Object.fromEntries(
    Object.entries(RULE_SCOPE_PARAM).map(([key, param]) => [param, (scope?.[key] || []).map(Number)])
  ), [scope]);
  const scopeKey = JSON.stringify(scopeParams);

  useEffect(() => {
    if (!enabled) {
      setLevel({ options: [], state: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setLevel((prev) => ({ options: prev.options, state: 'loading' }));
    loadRuleItems(kind, JSON.parse(scopeKey))
      .then((items) => {
        if (cancelled) return;
        const seen = new Set();
        const options = items
          .filter((rule) => rule.rule_id != null && rule.rule_id !== '')
          .filter((rule) => {
            const id = String(rule.rule_id);
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          })
          .map((rule) => ({
            id: String(rule.rule_id),
            name: rule.name || `Правило ${rule.rule_id}`,
            parentId: rule.camera_id == null ? '' : String(rule.camera_id),
            parentName: '',
          }));
        setLevel({ options, state: options.length ? 'ready' : 'empty' });
      })
      .catch((error) => {
        if (cancelled) return;
        const status = error?.response?.status;
        setLevel({ options: [], state: status === 403 || status === 401 ? 'forbidden' : 'error' });
      });
    return () => { cancelled = true; };
  }, [enabled, kind, scopeKey, token]);

  return { ...level, reload: useCallback(() => setToken((value) => value + 1), []) };
};

/**
 * Подпись камеры у правила (дополнение к BM-FE-TZ-002C, п. 2): picker отдаёт
 * только camera_id. Имя берётся из уже загруженных опций камер, остальные —
 * из общего кэша камер (useCameraNames); без имени — «Камера #ID». Админский
 * список правил ради имени не запрашивается.
 */
const useRuleCameraNames = (rules, cameras) => {
  const knownNames = useMemo(
    () => Object.fromEntries(cameras.options.map((option) => [option.id, option.name])),
    [cameras.options]
  );
  const missing = useMemo(() => {
    const ids = new Set(rules.options.map((option) => option.parentId).filter((id) => id && !knownNames[id]));
    return [...ids].map((id) => ({ camera_id: id }));
  }, [rules.options, knownNames]);
  const { cameraLabel } = useCameraNames(missing);

  return useMemo(() => {
    if (rules.options.length === 0) return rules;
    const options = rules.options.map((option) => (option.parentId
      ? { ...option, parentName: withCameraWord(knownNames[option.parentId] || cameraLabel(option.parentId)) }
      : option));
    return { ...rules, options };
  }, [rules, knownNames, cameraLabel]);
};

/**
 * Опции всех уровней scope одним объектом: { factoryIds: {options, state,
 * reload}, ... }. Выбор хранит вызывающая страница — здесь только списки.
 */
export const useScopeOptions = (
  scope,
  { includeRules = false, rulesKind = 'violation', includeZones = false, enabled = true } = {}
) => {
  const factories = useFactoryOptions(enabled);
  const workshops = useChildOptions('workshopIds', factories.options, scope.factoryIds || [], enabled);
  const sections = useChildOptions('sectionIds', workshops.options, scope.workshopIds || [], enabled);
  const rooms = useChildOptions('roomIds', sections.options, scope.sectionIds || [], enabled);
  const cameras = useChildOptions('cameraIds', rooms.options, scope.roomIds || [], enabled);
  const zones = useChildOptions('zoneIds', cameras.options, scope.cameraIds || [], enabled && includeZones);
  const rules = useRuleCameraNames(useRuleOptions(scope, rulesKind, enabled && includeRules), cameras);

  return useMemo(() => ({
    factoryIds: factories,
    workshopIds: workshops,
    sectionIds: sections,
    roomIds: rooms,
    cameraIds: cameras,
    zoneIds: zones,
    ruleIds: rules,
  }), [factories, workshops, sections, rooms, cameras, zones, rules]);
};

export { PARENT_OF };
export default useScopeOptions;
